import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

import { PageLoadError } from "../components/PageLoadError";
import { PageHeader } from "../components/pages/PageHeader";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import {
  AuditorService,
  type AuditorCertificationDetail,
  type AuditorChecklistAnswer,
  type AuditorChecklistQuestion,
} from "../services/auditorService";
import { ChecklistService } from "../services/checklistService";

type AnswerValue = "y" | "n" | "x";

const answerLabel = (answer: AuditorChecklistAnswer | undefined): string => {
  if (!answer?.answer) return "Unanswered";
  if (answer.answer === "y") return "Yes";
  if (answer.answer === "n") return "No";
  return "N/A";
};

const naReasonLabel = (answer: AuditorChecklistAnswer | undefined): string | null => {
  if (!answer || answer.answer !== "x") {
    return null;
  }

  return answer.na_reason_label || (answer.na_reason ? `Reason ${answer.na_reason}` : null);
};

const answerMap = (
  answers: AuditorChecklistAnswer[]
): Record<number, AnswerValue> => {
  return answers.reduce<Record<number, AnswerValue>>((acc, answer) => {
    if (answer.answer) {
      acc[answer.checklist_id] = answer.answer;
    }
    return acc;
  }, {});
};

const naReasonMap = (
  answers: AuditorChecklistAnswer[]
): Record<number, string> => {
  return answers.reduce<Record<number, string>>((acc, answer) => {
    if (typeof answer.na_reason === "number") {
      acc[answer.checklist_id] = answer.na_reason.toString();
    }
    return acc;
  }, {});
};

const naCommentMap = (
  answers: AuditorChecklistAnswer[]
): Record<number, string> => {
  return answers.reduce<Record<number, string>>((acc, answer) => {
    if (answer.na_comment) {
      acc[answer.checklist_id] = answer.na_comment;
    }
    return acc;
  }, {});
};

const SubmissionLabel = ({ revision }: { revision: number | null }) => {
  if (!revision || revision <= 1) {
    return null;
  }

  return <span className="badge badge-outline badge-sm">Submission {revision}</span>;
};

const LabChecklistTable = ({ detail }: { detail: AuditorCertificationDetail }) => {
  const answersById = new Map(
    detail.lab_checklist_answers.map((answer) => [answer.checklist_id, answer])
  );

  return (
    <Card>
      <div className="card-body">
        <div className="card-title justify-between gap-3">
          <span>Submitted Lab Checklist</span>
          <SubmissionLabel revision={detail.lab_checklist_revision} />
        </div>
        {detail.lab_checklist_comments && (
          <div className="mb-4 rounded-lg border border-base-300 bg-base-100 p-4">
            <p className="text-sm font-medium text-base-content/70">
              Lab checklist comment
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm">
              {detail.lab_checklist_comments}
            </p>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Question</th>
                <th>Answer</th>
              </tr>
            </thead>
            <tbody>
              {detail.lab_checklist_questions.map((question) => (
                (() => {
                  const answer = answersById.get(question.id);
                  const reason = naReasonLabel(answer);

                  return (
                    <tr key={question.id}>
                      <td>{question.question}</td>
                      <td>
                        <div>{answerLabel(answer)}</div>
                        {reason && (
                          <p className="mt-1 text-xs text-base-content/70">
                            Reason: {reason}
                          </p>
                        )}
                        {answer?.na_comment && (
                          <p className="mt-1 whitespace-pre-wrap text-xs text-base-content/70">
                            {answer.na_comment}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })()
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

const AuditorChecklistTable = ({
  questions,
  responses,
  naReasons,
  naComments,
  dirtyNaDetails,
  disabled,
  revision,
  onAnswer,
  onNaReasonChange,
  onNaCommentChange,
  onNaDetailsSave,
}: {
  questions: AuditorChecklistQuestion[];
  responses: Record<number, AnswerValue>;
  naReasons: Record<number, string>;
  naComments: Record<number, string>;
  dirtyNaDetails: Record<number, boolean>;
  disabled: boolean;
  revision: number | null;
  onAnswer: (questionId: number, answer: AnswerValue) => void;
  onNaReasonChange: (questionId: number, reason: string) => void;
  onNaCommentChange: (questionId: number, comment: string) => void;
  onNaDetailsSave: (questionId: number) => void;
}) => {
  return (
    <Card>
      <div className="card-body">
        <div className="card-title justify-between gap-3">
          <span>Auditor Checklist</span>
          <SubmissionLabel revision={revision} />
        </div>
        <div className="overflow-x-auto">
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Question</th>
                <th>Yes</th>
                <th>No</th>
                <th>N/A</th>
                <th>Suggestion</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((question) => (
                <tr key={question.id}>
                  <td>
                    <div>{question.question}</div>
                    {responses[question.id] === "x" && (
                      <div className="mt-3 flex max-w-xl flex-col gap-2">
                        <select
                          className="select select-bordered select-sm w-full max-w-xs"
                          value={naReasons[question.id] || ""}
                          onChange={(event) =>
                            onNaReasonChange(question.id, event.target.value)
                          }
                          disabled={disabled}
                        >
                          <option value="">Select reason...</option>
                          <option value="1">Internal Policy</option>
                          <option value="2">OHS Rules</option>
                          <option value="3">Building/Equipment Limits</option>
                          <option value="4">External Control</option>
                          <option value="5">Other</option>
                        </select>
                        <textarea
                          className="textarea textarea-bordered textarea-sm w-full"
                          value={naComments[question.id] || ""}
                          onChange={(event) =>
                            onNaCommentChange(question.id, event.target.value)
                          }
                          placeholder="Add a short note about why this item is not applicable."
                          disabled={disabled}
                        />
                        <button
                          className="btn btn-primary btn-sm w-fit"
                          disabled={disabled || !naReasons[question.id]}
                          onClick={() => onNaDetailsSave(question.id)}
                        >
                          Save N/A details
                        </button>
                        {dirtyNaDetails[question.id] && (
                          <p className="text-xs text-base-content/60">
                            Save these N/A details before submitting the auditor checklist.
                          </p>
                        )}
                      </div>
                    )}
                  </td>
                  {(["y", "n", "x"] as const).map((value) => (
                    <td key={value}>
                      <input
                        type="radio"
                        className="radio radio-primary"
                        checked={responses[question.id] === value}
                        disabled={disabled}
                        onChange={() => onAnswer(question.id, value)}
                      />
                    </td>
                  ))}
                  <td>{question.suggestion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

export const AuditorCertificationPage = () => {
  const { certId } = useParams<{ certId: string }>();
  const certificationId = Number(certId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [responses, setResponses] = useState<Record<number, AnswerValue>>({});
  const [naReasons, setNaReasons] = useState<Record<number, string>>({});
  const [naComments, setNaComments] = useState<Record<number, string>>({});
  const [dirtyNaDetails, setDirtyNaDetails] = useState<Record<number, boolean>>({});
  const [comments, setComments] = useState("");
  const [commentError, setCommentError] = useState(false);
  const pendingAnswerSaves = useRef<Set<Promise<unknown>>>(new Set());

  const detailQuery = useQuery({
    queryKey: ["auditor:certification", certificationId],
    queryFn: () => AuditorService.getCertificationDetail(certificationId),
    enabled: Number.isFinite(certificationId),
    retryOnMount: true,
    refetchOnMount: "always",
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (detailQuery.data) {
      setResponses(answerMap(detailQuery.data.auditor_checklist_answers));
      setNaReasons(naReasonMap(detailQuery.data.auditor_checklist_answers));
      setNaComments(naCommentMap(detailQuery.data.auditor_checklist_answers));
      setDirtyNaDetails({});
    }
  }, [detailQuery.data]);

  const invalidateDetail = () => {
    void queryClient.invalidateQueries({
      queryKey: ["auditor:certification", certificationId],
    });
    void queryClient.invalidateQueries({ queryKey: ["auditor:todo"] });
  };

  const saveAnswerMutation = useMutation({
    mutationFn: ({
      questionId,
      answer,
      naReason,
      naComment,
    }: {
      questionId: number;
      answer: AnswerValue;
      naReason?: number;
      naComment?: string;
    }) => {
      if (!detailQuery.data?.auditor_checklist_instance_id) {
        throw new Error("Auditor checklist not loaded");
      }
      return ChecklistService.saveChecklistInstanceAnswer(
        certificationId,
        detailQuery.data.auditor_checklist_instance_id,
        questionId,
        answer,
        naReason,
        naComment,
      );
    },
    onError: (error) => {
      toast.error(error.message || "Failed to save answer");
      invalidateDetail();
    },
  });

  const submitAuditorChecklistIfNeeded = async () => {
    const detail = detailQuery.data;
    if (!detail?.auditor_checklist_instance_id) {
      throw new Error("Auditor checklist not loaded");
    }

    if (detail.auditor_checklist_submitted) {
      return;
    }

    const unansweredQuestion = detail.auditor_checklist_questions.find(
      (question) => !responses[question.id]
    );
    if (unansweredQuestion) {
      throw new Error("Answer every auditor checklist question before continuing.");
    }

    const incompleteNaQuestion = detail.auditor_checklist_questions.find(
      (question) => responses[question.id] === "x" && !naReasons[question.id]
    );
    if (incompleteNaQuestion) {
      throw new Error("Save N/A details for every N/A auditor checklist answer.");
    }

    const unsavedNaQuestion = detail.auditor_checklist_questions.find(
      (question) => responses[question.id] === "x" && dirtyNaDetails[question.id]
    );
    if (unsavedNaQuestion) {
      throw new Error("Save N/A details before continuing.");
    }

    await Promise.allSettled(Array.from(pendingAnswerSaves.current));
    await Promise.all(
      detail.auditor_checklist_questions.map((question) => {
        const answer = responses[question.id]!;

        return ChecklistService.saveChecklistInstanceAnswer(
          certificationId,
          detail.auditor_checklist_instance_id!,
          question.id,
          answer,
          answer === "x" ? Number(naReasons[question.id]) : undefined,
          answer === "x" ? naComments[question.id]?.trim() || undefined : undefined,
        );
      })
    );

    await ChecklistService.submitChecklistInstance(
      certificationId,
      detail.auditor_checklist_instance_id,
    );
  };

  const certifyMutation = useMutation({
    mutationFn: async () => {
      await submitAuditorChecklistIfNeeded();
      return AuditorService.certify(certificationId);
    },
    onSuccess: () => {
      toast.success("Certification approved");
      navigate("/dashboard");
    },
    onError: (error) => toast.error(error.message || "Failed to certify"),
  });

  const requestReviewMutation = useMutation({
    mutationFn: async () => {
      await submitAuditorChecklistIfNeeded();
      return ChecklistService.requestFurtherReview(certificationId, comments);
    },
    onSuccess: () => {
      toast.success("Further review requested");
      navigate("/dashboard");
    },
    onError: (error) => toast.error(error.message || "Failed to request review"),
  });

  const score = useMemo(() => {
    const values = Object.values(responses);
    const yes = values.filter((answer) => answer === "y").length;
    const no = values.filter((answer) => answer === "n").length;
    const eligible = yes + no;
    return eligible > 0 ? Math.round((yes / eligible) * 100) : 0;
  }, [responses]);

  const auditorChecklistNoRate = useMemo(() => {
    const values = Object.values(responses);
    const yesCount = values.filter((answer) => answer === "y").length;
    const noCount = values.filter((answer) => answer === "n").length;
    const eligible = yesCount + noCount;

    return eligible > 0 ? noCount / eligible : 0;
  }, [responses]);

  if (detailQuery.isLoading) {
    return <div className="skeleton h-4 w-full"></div>;
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <PageLoadError
        message="Unable to load audit."
        onRetry={() => detailQuery.refetch()}
        isRetrying={detailQuery.isFetching}
      />
    );
  }

  const detail = detailQuery.data;
  const auditorQuestions = detail.auditor_checklist_questions;
  const auditorChecklistSubmitted = detail.auditor_checklist_submitted;
  const allQuestionsAnswered = auditorQuestions.every(
    (question) => responses[question.id]
  );
  const allNaResponsesHaveReasons = auditorQuestions.every(
    (question) => responses[question.id] !== "x" || Boolean(naReasons[question.id])
  );
  const allNaDetailsSaved = auditorQuestions.every(
    (question) => responses[question.id] !== "x" || !dirtyNaDetails[question.id]
  );
  const finalActionPending =
    certifyMutation.isPending || requestReviewMutation.isPending;
  const canCompleteAuditorReview =
    allQuestionsAnswered &&
    allNaResponsesHaveReasons &&
    allNaDetailsSaved &&
    detail.certification_state === "under_review" &&
    !finalActionPending;
  const canCertify =
    canCompleteAuditorReview && auditorChecklistNoRate < 0.2;
  const canRequestReview = canCompleteAuditorReview;

  const handleAnswer = (questionId: number, answer: AnswerValue) => {
    setResponses((prev) => ({ ...prev, [questionId]: answer }));
    if (answer === "x") {
      setDirtyNaDetails((prev) => ({ ...prev, [questionId]: true }));
      return;
    }

    setNaReasons((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    setNaComments((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    setDirtyNaDetails((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });

    const savePromise = saveAnswerMutation.mutateAsync({ questionId, answer });
    pendingAnswerSaves.current.add(savePromise);
    savePromise.then(
      () => pendingAnswerSaves.current.delete(savePromise),
      () => pendingAnswerSaves.current.delete(savePromise)
    );
  };

  const handleNaReasonChange = (questionId: number, reason: string) => {
    setNaReasons((prev) => ({ ...prev, [questionId]: reason }));
    setDirtyNaDetails((prev) => ({ ...prev, [questionId]: true }));
  };

  const handleNaCommentChange = (questionId: number, comment: string) => {
    setNaComments((prev) => ({ ...prev, [questionId]: comment }));
    setDirtyNaDetails((prev) => ({ ...prev, [questionId]: true }));
  };

  const handleNaDetailsSave = (questionId: number) => {
    const reason = naReasons[questionId];
    if (!reason) {
      toast.error("Select an N/A reason before saving");
      return;
    }

    const savePromise = saveAnswerMutation.mutateAsync({
      questionId,
      answer: "x",
      naReason: Number(reason),
      naComment: naComments[questionId]?.trim() || undefined,
    });
    pendingAnswerSaves.current.add(savePromise);
    savePromise.then(
      () => {
        pendingAnswerSaves.current.delete(savePromise);
        setDirtyNaDetails((prev) => {
          const next = { ...prev };
          delete next[questionId];
          return next;
        });
      },
      () => pendingAnswerSaves.current.delete(savePromise)
    );
  };

  const handleRequestReview = () => {
    if (!comments.trim()) {
      setCommentError(true);
      return;
    }
    setCommentError(false);
    requestReviewMutation.mutate();
  };

  return (
    <div className="l0-layout space-y-6">
      <PageHeader
        pageTitle={`Audit ${detail.lab_name}`}
        backButton={
          <Button variant="neutral" small onClick={() => navigate("/dashboard")}>
            Back to audits
          </Button>
        }
      >
        <p className="text-sm text-base-content/70">
          {detail.certification_level} certification is {detail.certification_state}.
        </p>
      </PageHeader>

      <LabChecklistTable detail={detail} />

      <AuditorChecklistTable
        questions={auditorQuestions}
        responses={responses}
        naReasons={naReasons}
        naComments={naComments}
        dirtyNaDetails={dirtyNaDetails}
        disabled={auditorChecklistSubmitted}
        revision={detail.auditor_checklist_revision}
        onAnswer={handleAnswer}
        onNaReasonChange={handleNaReasonChange}
        onNaCommentChange={handleNaCommentChange}
        onNaDetailsSave={handleNaDetailsSave}
      />

      <Card>
        <div className="card-body space-y-4">
          <div>
            <h2 className="card-title">Audit Result</h2>
            <p className="text-sm text-base-content/70">
              Current score: {score}%.
              {auditorChecklistSubmitted
                ? " Auditor checklist has been submitted."
                : " Complete the auditor checklist before certifying or requesting review."}
            </p>
            {allQuestionsAnswered && (
              <p className="mt-1 text-sm text-base-content/70">
                Auditor checklist NO answers: {(auditorChecklistNoRate * 100).toFixed(0)}%.
                {auditorChecklistNoRate < 0.2
                  ? " Less than 20% NO answers, so certification can be submitted."
                  : " 20% or more NO answers, so certification is blocked, but further review can still be requested."}
              </p>
            )}
          </div>

          <div>
            <textarea
              className={`textarea textarea-bordered w-full${commentError ? " border-error focus:border-error" : ""}`}
              placeholder="Comments for request-review email"
              value={comments}
              onChange={(event) => {
                setComments(event.target.value);
                if (commentError && event.target.value.trim()) setCommentError(false);
              }}
            />
            <p className="mt-1 text-sm text-base-content/60">
              A comment is required when requesting further review.
            </p>
            {commentError && (
              <p className="mt-1 text-sm text-error">Comment is required to request further review.</p>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              className="btn btn-primary"
              disabled={!canCertify}
              onClick={() => certifyMutation.mutate()}
            >
              Certify
            </button>
            <button
              className="btn btn-warning"
              disabled={!canRequestReview}
              onClick={handleRequestReview}
            >
              Request Further Review
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};
